// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module SharedFsl
 * @package @geenius/analytics
 * @description Centralizes the shared FSL infrastructure inputs for the
 * analytics package so every runtime variant consumes the same translation
 * keys, localized labels, and SEO metadata builder.
 */
/// <reference path="./externals.d.ts" />
import { t as translate } from "@geenius/i18n";
import en from "./locales/en.json";
import type { DeviceInfo } from "./types";

/**
 * Interpolation values accepted by analytics translation helpers.
 */
export type AnalyticsParams = Record<string, string | number>;

/**
 * Minimal translator contract used by package-owned analytics copy helpers.
 *
 * @param key Translation key in the analytics namespace.
 * @param params Optional interpolation values.
 * @returns Localized copy for the requested key.
 */
export type AnalyticsTranslator = (
  key: string,
  params?: AnalyticsParams,
) => string;

/**
 * English fallback table used when the host application does not provide a translation.
 */
export const ANALYTICS_DEFAULT_TRANSLATIONS = en;

/**
 * Translation keys used to render stable device labels across every analytics variant.
 */
export const ANALYTICS_DEVICE_TRANSLATION_KEYS: Readonly<
  Record<DeviceInfo["device"], string>
> = {
  desktop: "analytics.devices.desktop",
  tablet: "analytics.devices.tablet",
  mobile: "analytics.devices.mobile",
};

const ANALYTICS_SITE_URL = "https://geenius.dev";
const ANALYTICS_SITE_NAME = "Geenius Analytics";
const ANALYTICS_OG_IMAGE = "https://geenius.dev/og/analytics.png";

function buildPluralKey(key: string, count: number): string {
  if (count === 0) {
    return `${key}_zero`;
  }

  if (count === 1) {
    return `${key}_one`;
  }

  return `${key}_other`;
}

function humanizeToken(value: string): string {
  return value
    .replaceAll(/[-_]+/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function translateWithFallback(
  translator: AnalyticsTranslator,
  key: string,
  params?: AnalyticsParams,
): string {
  const translated = translator(key, params);

  if (translated !== key) {
    return translated;
  }

  return translateAnalytics(key, params);
}

/**
 * Resolves an analytics translation key against the package fallback table.
 *
 * @param key Translation key in the analytics namespace.
 * @param params Optional interpolation values.
 * @returns Localized analytics copy.
 */
export function translateAnalytics(
  key: string,
  params?: AnalyticsParams,
): string {
  return translate(key, ANALYTICS_DEFAULT_TRANSLATIONS, params);
}

/**
 * Resolves a pluralized analytics translation with fallback to the base key.
 *
 * @param key Base translation key without the plural suffix.
 * @param count Count used for plural selection and interpolation.
 * @param params Additional interpolation values.
 * @returns Localized plural copy.
 */
export function pluralizeAnalytics(
  key: string,
  count: number,
  params?: AnalyticsParams,
): string {
  const interpolationParams = { count, ...params };
  const pluralKey = buildPluralKey(key, count);
  const pluralValue = translate(
    pluralKey,
    ANALYTICS_DEFAULT_TRANSLATIONS,
    interpolationParams,
  );

  if (pluralValue !== pluralKey) {
    return pluralValue;
  }

  return translate(key, ANALYTICS_DEFAULT_TRANSLATIONS, interpolationParams);
}

/**
 * Returns a localized label for a normalized analytics device token.
 *
 * @param device Device token from analytics payloads.
 * @param translator Optional host translator used before the fallback table.
 * @returns Localized or humanized device label.
 */
export function getAnalyticsDeviceLabel(
  device: string,
  translator: AnalyticsTranslator = translateAnalytics,
): string {
  const key = ANALYTICS_DEVICE_TRANSLATION_KEYS[device as DeviceInfo["device"]];

  return key ? translateWithFallback(translator, key) : humanizeToken(device);
}

/**
 * Formats a timestamp-like value for compact analytics UI surfaces.
 *
 * @param value Date-compatible value to render.
 * @param locale Optional BCP 47 locale override.
 * @returns Locale-aware short time string.
 */
export function formatAnalyticsTime(
  value: number | string,
  locale?: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    timeStyle: "short",
  }).format(new Date(value));
}

/**
 * Inputs used to build SEO metadata for analytics page compositions.
 */
export interface BuildAnalyticsSeoMetaOptions {
  title: string;
  description: string;
  path: string;
  keywords: string[];
}

/**
 * SEO metadata emitted by the shared analytics page metadata builder.
 */
export interface SEOMeta {
  title: string;
  description: string;
  canonical: string;
  keywords: string[];
  image: {
    url: string;
    alt: string;
  };
  type: "website";
  jsonLd: Record<string, unknown>;
}

function buildCanonical(path: string, siteUrl: string): string {
  return new URL(path, siteUrl).toString();
}

/**
 * Builds canonical SEO metadata for analytics dashboard pages.
 *
 * @param options Page title, description, route path, and keyword list.
 * @returns Metadata object containing canonical URL, Open Graph image, and JSON-LD.
 */
export function buildAnalyticsSeoMeta(
  options: BuildAnalyticsSeoMetaOptions,
): SEOMeta {
  const canonical = buildCanonical(options.path, ANALYTICS_SITE_URL);

  return {
    title: `${options.title} | ${ANALYTICS_SITE_NAME}`,
    description: options.description,
    canonical,
    keywords: options.keywords,
    image: {
      url: ANALYTICS_OG_IMAGE,
      alt: translateAnalytics("analytics.seo.imageAlt"),
    },
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: options.title,
      description: options.description,
      url: canonical,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
    },
  };
}
